// Shared fetch helpers for the web app. Unlike the Flutter ApiClient, the
// web useAuth() has no built-in 401 retry — `authedFetch` adds a single retry
// after re-hydrating the session, so a long-open tab whose access token aged
// out doesn't fail the next authenticated GET/PUT.

export type ApiError = Error & { code: string; status: number };

export async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = `${res.status}`;
    let code = '';
    try {
      const body = await res.json();
      msg = body?.error?.message || msg;
      code = body?.error?.code || '';
    } catch {}
    const e = new Error(msg) as ApiError;
    e.code = code;
    e.status = res.status;
    throw e;
  }
  // 204 / empty bodies → null
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

function withAuth(token: string | null, init: RequestInit = {}): RequestInit {
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string>),
  };
  // FormData sets its own multipart boundary — never override it.
  const isForm = typeof FormData !== 'undefined' && init.body instanceof FormData;
  if (init.body && !isForm && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  return { ...init, headers, credentials: 'include' };
}

/**
 * Authenticated fetch with one transparent retry on 401.
 *
 * `getToken` is read lazily so the retry picks up the freshly-minted token
 * produced by `hydrate()` (which updates the auth context out-of-band).
 */
export async function authedFetch<T>(
  getToken: () => string | null,
  hydrate: () => Promise<void>,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  let res = await fetch(path, withAuth(getToken(), init));
  if (res.status === 401) {
    await hydrate();
    res = await fetch(path, withAuth(getToken(), init));
  }
  return handle<T>(res);
}

/** Public GET (bundle, distribution, partners) — no auth needed. */
export async function getJSON<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'include' });
  return handle<T>(res);
}
